import React, { memo } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import AdminStatusPill from '../../../components/admin/AdminStatusPill';
import AdminSurface from '../../../components/admin/AdminSurface';
import type { EmailPipelineRecordsTableProps } from '../../../types/features/emailPipeline';
import { formatDateTimeMMDDYYYY } from '../../../utils/formatUtils';
import { getStatusLabel } from '../../../utils/formatters/statusFormatters';
import {
  adminBorders,
  adminColors,
  adminControlHeights,
  adminTypography,
} from '../../../styles/adminDesignTokens';
import {
  adminCheckboxSx,
  adminCombineSx,
  adminEmptyStateSx,
  adminTableSx,
} from '../../../styles/adminComponentStyles';
import { getEmailPipelineStatusTone, getStoreDisplayName } from '../mappers/emailPipelineFormatters';
import { PAGE_SIZE } from '../mappers/emailPipelineNormalizers';

const EmailPipelineRecordsTable: React.FC<EmailPipelineRecordsTableProps> = ({
  model,
  actions,
}) => {
  const allSelected = model.records.length > 0 && model.selectedIdsCount === model.records.length;

  return (
    <>
      <TableContainer
        component={AdminSurface}
        sx={adminCombineSx(adminTableSx, {
          border: 'none',
          borderRadius: 0,
          overflowX: 'auto',
        })}
      >
        <Table
          size="small"
          aria-label="email pipeline records"
          sx={adminCombineSx(adminTableSx, {
            minWidth: 1120,
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': {
              padding: '8px 12px !important',
            },
            '& .MuiTableBody-root .MuiTableRow-root': {
              height: 42,
            },
            '& .MuiTableBody-root .MuiTableRow-root.AdminClickableRow': {
              cursor: 'pointer',
            },
            '& .MuiTableCell-body': {
              py: 0.75,
            },
          })}
        >
          <TableHead sx={{ '& th': { padding: '8px 12px !important', textTransform: 'uppercase' } }}>
            <TableRow>
              <TableCell sx={{ width: 44 }}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={!allSelected && model.selectedIdsCount > 0}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(_, checked) => actions.onToggleSelectAll(checked)}
                  sx={adminCheckboxSx}
                />
              </TableCell>
              <TableCell sx={{ width: 100 }}>Date</TableCell>
              <TableCell sx={{ width: 180 }}>Store</TableCell>
              <TableCell sx={{ width: 220 }}>Subject</TableCell>
              <TableCell sx={{ width: 180 }}>From</TableCell>
              <TableCell sx={{ width: 100 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {model.recordsLoading ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ border: 0 }}>
                  <Box py={3} display="flex" justifyContent="center">
                    <CircularProgress size={26} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : model.records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ border: 0, p: 1.5 }}>
                  <Box sx={adminCombineSx(adminEmptyStateSx, { border: 0, minHeight: 86 })}>
                    <Typography className="AdminEmptyState-title">No email records found</Typography>
                    <Typography className="AdminEmptyState-copy">Try a different status, store, or date range.</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              model.records.map((record) => (
                <TableRow
                  key={record.id}
                  hover
                  className="AdminClickableRow"
                  onClick={() => actions.onViewDetails(record.message_id)}
                >
                  <TableCell>
                    <Checkbox
                      checked={actions.isRecordSelected(record.id)}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(_, checked) => actions.onToggleRowSelection(record.id, checked)}
                      sx={adminCheckboxSx}
                    />
                  </TableCell>
                  <TableCell>{formatDateTimeMMDDYYYY(record.received_at)}</TableCell>
                  <TableCell>
                    <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>
                      {getStoreDisplayName(record.store_name, record.store_key)}
                    </Typography>
                    <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText }}>
                      {record.to_email || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ ...adminTypography.body, color: adminColors.text }}>
                      {record.subject || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ ...adminTypography.body, color: adminColors.text }} noWrap>
                      {record.from_email || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <AdminStatusPill
                      label={getStatusLabel(record.status)}
                      tone={getEmailPipelineStatusTone(record.status)}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={model.totalRecords}
        page={Math.max(model.page - 1, 0)}
        onPageChange={(_, nextPage) => actions.onPageChange(nextPage + 1)}
        rowsPerPage={PAGE_SIZE}
        onRowsPerPageChange={() => {}}
        rowsPerPageOptions={[PAGE_SIZE]}
        labelRowsPerPage="Rows per page"
        sx={{
          borderTop: adminBorders.divider,
          minHeight: adminControlHeights.tablePagination,
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            ...adminTypography.helper,
            color: adminColors.mutedText,
          },
          '& .MuiTablePagination-toolbar': {
            minHeight: adminControlHeights.tablePagination,
          },
        }}
      />
    </>
  );
};

export default memo(EmailPipelineRecordsTable);
